import React, { useCallback } from 'react';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import config from 'config';
import LoadingSection from 'components/LoadingSection';
import ShipperDetail from './shipper-detail';

import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { constants, getSpecificClientConfig } from 'redux/modules/client-config/selectors';
import {
  EditShipperPage,
  EditShipperUserPage,
  NewShipperPage,
  NewShipperUserPage,
  ShippersPage,
} from '@ontruck/fe-core';
import { HOST } from 'tenant/host_config';
import { getShipperModuleConfig } from 'tenant/bussines';
import { useHistory } from 'react-router-dom';

const Shippers = ({ match, loading, className, intl }) => {
  const history = useHistory();
  const location = useLocation();
  const token = useSelector((state) => state?.user?.access_token);

  const shippersPageTranslations = intl.messages.components.ShippersModule.shippers_list;
  const shipperFormTranslations = intl.messages.components.ShippersModule.shipper_form;
  const newShipperPageTranslations = intl.messages.components.ShippersModule.new_shipper;
  const editShipperPageTranslations = intl.messages.components.ShippersModule.edit_shipper;

  // shipper user
  const shipperUserFormTranslations = intl.messages.components.ShippersModule.shipper_user_form;
  const newShipperUserPageTranslations = intl.messages.components.ShippersModule.new_shipper_user;
  const editShipperUserPageTranslations = intl.messages.components.ShippersModule.edit_shipper_user;
  const shipperModuleConfig = getShipperModuleConfig(HOST);

  const shippersCreationEnabled = useSelector((state) =>
    getSpecificClientConfig(state, constants.SHIPPERS_CREATION_ENABLED)
  );

  const hiddenColumns = shipperModuleConfig.list.hidden_columns;
  const showIncludeHiddenFilter = shipperModuleConfig.list.show_include_hidden_filter;
  const hiddenFields = shipperModuleConfig.creation.hidden_fields;
  const showNewShipperDetail = shipperModuleConfig.detail.showNewDetail;

  const consumerConfig = {
    api_url: `${config.BASE_URL}/`,
    token,
    on_success: () => {},
    on_discard: () => {},
  };

  const handleGoToNewShipper = () => {
    history.push({ pathname: `${config.BASE_ROUTE}shipper/new/` });
  };

  const handleGoToShippersList = useCallback(() => {
    history.push('/shipper', { from: location.pathname });
  }, [history, location.pathname]);

  const handleGoToShipperDetail = useCallback(
    (shipperId) => {
      history.push({
        pathname: `${config.BASE_ROUTE}shipper/${shipperId}`,
        state: { from: location.pathname },
      });
    },
    [history, location.pathname]
  );

  const handleAddUserToShipper = useCallback(
    (shipperId) => {
      history.push({
        pathname: `${config.BASE_ROUTE}shipper/${shipperId}/shipper-user/new/`,
        state: { from: location.pathname },
      });
    },
    [history, location.pathname]
  );

  const handleGoToEditShipperUser = useCallback(
    (shipperId, shipperUserId) => {
      history.push({
        pathname: `${config.BASE_ROUTE}shipper/shipper-user/${shipperId}/${shipperUserId}/`,
        state: { from: location.pathname },
      });
    },
    [history, location.pathname]
  );

  if (loading) {
    return <LoadingSection />;
  }

  return (
    <div className={className}>
      <Switch>
        <Route
          exact
          path={match.url}
          render={() => (
            <ShippersPage
              shippersPageTranslations={shippersPageTranslations}
              consumerConfig={consumerConfig}
              onGoToNewShipper={handleGoToNewShipper}
              onGoToShipperDetail={handleGoToShipperDetail}
              hiddenColumns={hiddenColumns}
              showIncludeHiddenFilter={showIncludeHiddenFilter}
              showNewShipperButton={shippersCreationEnabled}
            />
          )}
        />
        <Route
          path={`${match.url}/new/`}
          render={({ location }) => {
            if (!shippersCreationEnabled) return <Redirect to="/shipper" />;
            return (
              <NewShipperPageWrapper
                key={location.key}
                consumerConfig={consumerConfig}
                hiddenFields={hiddenFields}
                translations={newShipperPageTranslations}
                onGoToShippersList={handleGoToShippersList}
                onGoToShipperDetail={handleGoToShipperDetail}
                shipperFormPageTranslations={newShipperPageTranslations}
                shipperFormTranslations={shipperFormTranslations}
              />
            );
          }}
        />
        <Route
          path={`${match.url}/:shipperId/shipper-user/new/`}
          render={({ location, match }) => {
            if (showNewShipperDetail) {
              const { shipperId } = match.params;
              return (
                <NewShipperUserPage
                  key={location.key}
                  consumerConfig={consumerConfig}
                  hiddenFields={hiddenFields}
                  onGoToShippersList={handleGoToShippersList}
                  onGoToShipperDetail={handleGoToShipperDetail}
                  shipperUserFormPageTranslations={newShipperUserPageTranslations}
                  shipperUserFormTranslations={shipperUserFormTranslations}
                  shipperId={shipperId}
                />
              );
            }
            return <Redirect to="/shipper" />;
          }}
        />
        <Route
          path={`${match.url}/shipper-user/:shipperId/:shipperUserId/`}
          render={({ location, match }) => {
            if (showNewShipperDetail) {
              const { shipperId, shipperUserId } = match.params;
              return (
                <EditShipperUserPage
                  key={location.key}
                  consumerConfig={consumerConfig}
                  onGoToShippersList={handleGoToShippersList}
                  onGoToShipperDetail={handleGoToShipperDetail}
                  shipperUserFormPageTranslations={editShipperUserPageTranslations}
                  shipperUserFormTranslations={shipperUserFormTranslations}
                  shipperId={shipperId}
                  shipperUserId={shipperUserId}
                />
              );
            }
            return <Redirect to="/shipper" />;
          }}
        />
        <Route
          path={`${match.url}/:shipperId`}
          render={({ match }) => {
            if (showNewShipperDetail) {
              const { shipperId } = match.params;
              return (
                <EditShipperPage
                  shipperId={shipperId}
                  consumerConfig={consumerConfig}
                  hiddenFields={hiddenFields}
                  shipperFormPageTranslations={editShipperPageTranslations}
                  shipperFormTranslations={shipperFormTranslations}
                  onGoToShippersList={handleGoToShippersList}
                  onGoToShipperDetail={handleGoToShipperDetail}
                  onAddUser={handleAddUserToShipper}
                  onGoToEditShipperUser={handleGoToEditShipperUser}
                />
              );
            }
            return <ShipperDetail />;
          }}
        />
      </Switch>
    </div>
  );
};

const NewShipperPageWrapper = (props) => {
  const location = useLocation();
  const prevPathRef = React.useRef(location.state?.from);

  if (location.pathname !== props.match?.url && prevPathRef.current) {
    return null;
  }

  return <NewShipperPage {...props} />;
};

export default injectIntl(Shippers);
